import React from "react";
import { motion } from "framer-motion";
import {
  User, Compass, FileCheck2, GraduationCap, HeartHandshake,
  BedDouble, UtensilsCrossed, Sparkles, Wrench, MessageSquareHeart, Check,
} from "lucide-react";

const STEP_ICONS = [
  User, Compass, FileCheck2, GraduationCap, HeartHandshake,
  BedDouble, UtensilsCrossed, Sparkles, Wrench, MessageSquareHeart,
];

const STEP_LABELS = [
  "Basics", "Counselling", "Documentation", "Campus",
  "Local Support", "Hostel", "Food", "Overall", "Improve", "Final",
];

export default function WizardTimeline({ current, total, onJump, percent }) {
  return (
    <div data-testid="wizard-timeline" className="relative">
      <div className="flex items-end justify-between gap-3 mb-4 sm:mb-6">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.22em] text-saffron-600 font-medium truncate">
            Step {current + 1} of {total} · {STEP_LABELS[current]}
          </div>
          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl mt-1 text-stone-900 leading-tight">
            Your feedback journey
          </h2>
        </div>
        <div className="text-right shrink-0">
          <div className="font-serif text-2xl sm:text-3xl text-saffron-600 leading-none" data-testid="timeline-percent">
            {percent}<span className="text-sm sm:text-base text-stone-400 ml-0.5">%</span>
          </div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-stone-500 mt-1">complete</div>
        </div>
      </div>

      {/* Track */}
      <div className="relative pb-7 md:pb-8">
        <div className="absolute left-0 right-0 top-[18px] sm:top-[22px] h-[3px] bg-stone-100 rounded-full" />
        <motion.div
          className="absolute left-0 top-[18px] sm:top-[22px] h-[3px] rounded-full"
          style={{ background: "linear-gradient(90deg, #1E90D7 0%, #1E90D7 60%, #9DC827 100%)", boxShadow: "0 0 12px rgba(30,144,215,0.45)" }}
          initial={{ width: 0 }}
          animate={{ width: `${(current / (total - 1)) * 100}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />

        {/* Nodes */}
        <ol className="relative flex items-start justify-between">
          {Array.from({ length: total }).map((_, i) => {
            const Icon = STEP_ICONS[i] || Check;
            const isDone = i < current;
            const isActive = i === current;
            const isFuture = i > current;
            return (
              <li key={i} className="relative flex flex-col items-center" data-testid={`timeline-node-${i}`}>
                <button
                  type="button"
                  onClick={() => onJump?.(i)}
                  disabled={isFuture}
                  className={`relative z-10 group focus:outline-none ${isFuture ? "cursor-not-allowed" : "cursor-pointer"}`}
                  aria-label={`Go to step ${i + 1}: ${STEP_LABELS[i]}`}
                  data-testid={`timeline-jump-${i}`}
                >
                  {isActive && (
                    <motion.span
                      className="absolute inset-0 rounded-full bg-saffron-500/25"
                      animate={{ scale: [1, 1.7, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                  <span
                    className={[
                      "relative rounded-full flex items-center justify-center transition-all duration-300 border-2",
                      "w-8 h-8 sm:w-11 sm:h-11",
                      isDone
                        ? "bg-saffron-500 border-saffron-500 text-white shadow-md shadow-saffron-500/30 group-hover:scale-105"
                        : isActive
                        ? "bg-white border-saffron-500 text-saffron-600 shadow-lg shadow-saffron-500/25 ring-4 ring-saffron-500/15"
                        : "bg-white border-stone-200 text-stone-300",
                    ].join(" ")}
                  >
                    {isDone ? (
                      <Check className="w-3.5 h-3.5 sm:w-5 sm:h-5" strokeWidth={2.5} />
                    ) : (
                      <Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5" strokeWidth={1.75} />
                    )}
                  </span>
                </button>
                {/* Mini label — only on md+ to keep mobile clean */}
                <span
                  className={[
                    "hidden md:block absolute top-12 sm:top-14 text-[11px] whitespace-nowrap font-medium tracking-wide",
                    isActive ? "text-saffron-600" : isDone ? "text-stone-500" : "text-stone-300",
                  ].join(" ")}
                >
                  {STEP_LABELS[i]}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
